import { supabase } from '../lib/supabase';

// Plan + entitlement helpers. Everything plan-aware in the app goes through
// this module - DB-truth lookups for the user's plan, quota checks for
// premium features, and detection of the DB triggers' PLAN_CAP error code.
//
// Limits are *not* hard-coded here. The DB is the source of truth: the
// trigger functions and RPCs in the plan migrations own the numbers (8
// friends, 2 groups, 5 members per group). Planned rounds and Smart Schedule
// runs are uncapped (see 20260604120000_free_tier_retiering.sql) - on iOS the
// free tier is monetised by a rewarded ad-gate instead, which reads the
// `current` count these RPCs still return. The helpers below just call those
// RPCs and forward the result so a future limit change is a migration only.

const getCurrentUser = async (provided = null) => {
  if (provided) return provided;
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

/**
 * Read the user's plan state from `user_profiles`. Returns a normalised
 * snapshot that AuthContext caches and exposes to components.
 *
 * Shape: { plan, planPeriodEnd, planSource, calendarGrandfathered, isPro }
 *   - plan: 'free' | 'pro'
 *   - planPeriodEnd: Date | null
 *   - planSource: 'ios' | 'stripe' | 'manual' | null
 *   - calendarGrandfathered: boolean
 *   - isPro: derived - true iff plan === 'pro' AND not expired
 */
export const fetchPlan = async (currentUser = null) => {
  const user = await getCurrentUser(currentUser);
  if (!user) {
    return {
      plan: 'free',
      planPeriodEnd: null,
      planSource: null,
      calendarGrandfathered: false,
      isPro: false,
    };
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .select('plan, plan_period_end, plan_source, calendar_grandfathered')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching plan:', error);
    return {
      plan: 'free',
      planPeriodEnd: null,
      planSource: null,
      calendarGrandfathered: false,
      isPro: false,
    };
  }

  const plan = data?.plan || 'free';
  const planPeriodEnd = data?.plan_period_end ? new Date(data.plan_period_end) : null;
  const isPro = plan === 'pro' && (planPeriodEnd === null || planPeriodEnd > new Date());

  return {
    plan,
    planPeriodEnd,
    planSource: data?.plan_source || null,
    calendarGrandfathered: !!data?.calendar_grandfathered,
    isPro,
  };
};

// =========================================================================
// Quota helpers (per-feature, time-windowed)
// =========================================================================

/**
 * Check a per-feature quota for the current user.
 *
 * Currently supports featureKey = 'smart_schedule'. Returns
 * { allowed, plan, feature, current, limit, window, resetAt }. Limit/resetAt
 * are null for pro users.
 *
 * Callers should preflight with this and present the paywall when
 * `allowed === false`. After successful use of the feature, call
 * `logPlanUsage` so subsequent checks see the bumped count.
 */
export const checkPlanQuota = async (featureKey, currentUser = null) => {
  const user = await getCurrentUser(currentUser);
  if (!user) return { allowed: false, plan: 'free', feature: featureKey };

  const { data, error } = await supabase.rpc('check_plan_quota', {
    p_user_id: user.id,
    p_feature_key: featureKey,
  });

  if (error) {
    console.error('check_plan_quota RPC error:', error);
    // Fail-open: if the RPC itself errors, let the action proceed. The DB
    // triggers / state caps will still catch caps that have hard rails. For
    // quota-style features (smart_schedule) we'd rather log a server error
    // than block a paying user on a transient network blip.
    return { allowed: true, plan: 'unknown', feature: featureKey };
  }

  return {
    allowed: !!data.allowed,
    plan: data.plan,
    feature: data.feature,
    current: data.current,
    limit: data.limit,
    window: data.window,
    resetAt: data.reset_at ? new Date(data.reset_at) : null,
  };
};

export const logPlanUsage = async (featureKey, metadata = null, currentUser = null) => {
  const user = await getCurrentUser(currentUser);
  if (!user) return;
  const { error } = await supabase.rpc('log_plan_usage', {
    p_user_id: user.id,
    p_feature_key: featureKey,
    p_metadata: metadata,
  });
  if (error) console.error('log_plan_usage RPC error:', error);
};

// =========================================================================
// State-cap helpers (friends, groups, planned rounds)
// =========================================================================

const fetchCapacity = async (rpcName, currentUser) => {
  const user = await getCurrentUser(currentUser);
  if (!user) return { allowed: false, plan: 'free' };
  const { data, error } = await supabase.rpc(rpcName, { p_user_id: user.id });
  if (error) {
    console.error(`${rpcName} RPC error:`, error);
    return { allowed: true, plan: 'unknown' };
  }
  return {
    allowed: !!data.allowed,
    plan: data.plan,
    feature: data.feature,
    current: data.current,
    limit: data.limit,
  };
};

export const checkFriendCapacity = (currentUser = null) =>
  fetchCapacity('check_friend_capacity', currentUser);

export const checkGroupCapacity = (currentUser = null) =>
  fetchCapacity('check_group_capacity', currentUser);

export const checkPlannedRoundsCapacity = (currentUser = null) =>
  fetchCapacity('check_planned_rounds_capacity', currentUser);

/**
 * Per-group member-size cap. Free-tier owners cap their group at 5 members;
 * Pro owners are uncapped. Returns the same shape as the other capacity
 * helpers plus an `ownerIsPro` flag the UI can use to phrase the paywall
 * ("the group owner needs Pro" vs "you need Pro").
 */
export const checkGroupSizeCapacity = async (groupId) => {
  const { data, error } = await supabase.rpc('check_group_size_capacity', { p_group_id: groupId });
  if (error) {
    console.error('check_group_size_capacity RPC error:', error);
    return { allowed: true, plan: 'unknown' };
  }
  return {
    allowed: !!data.allowed,
    feature: data.feature,
    current: data.current,
    limit: data.limit,
    ownerIsPro: !!data.owner_is_pro,
  };
};

// =========================================================================
// PLAN_CAP error detection (defence in depth via DB triggers)
// =========================================================================
// Triggers raise `PLAN_CAP:<feature>[:<side>] (...)` on insert/update when a
// free user would exceed a cap. Useful as a safety net for any code path
// that bypassed the preflight (e.g. third-party RPC writes).

const PLAN_CAP_RE = /PLAN_CAP:([a-z_]+)(?::([a-z]+))?/i;

export const isPlanCapError = (err) => {
  if (!err) return false;
  const msg = err?.message || err?.error_description || '';
  return PLAN_CAP_RE.test(msg);
};

export const parsePlanCapError = (err) => {
  const msg = err?.message || err?.error_description || '';
  const match = msg.match(PLAN_CAP_RE);
  if (!match) return null;
  return { feature: match[1], side: match[2] || null };
};
